import { Button, Dialog, DialogActions, DialogTitle, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { jaJP } from '@mui/x-data-grid/locales';
import {
  DataGridPremium,
  GridColumnResizeParams,
  GridFilterItem,
  GridFilterModel,
  GridRowSelectionModel,
  GridSortModel
} from '@mui/x-data-grid-premium';
import { FC, memo, useEffect, useRef, useState } from 'react';
import { CustomLoadingOverlay } from 'src/components/utils/CustomLoadingOverlay';
import { DriverEntity } from 'src/entities/driverEntity';
import { RansackPredicateEntity } from 'src/entities/RansackPredicate.entity';
import { useDriversRequest } from 'src/hooks/useDrivers.request';
import { dataGridUtil } from 'src/utils/dataGrid.util';
import operatorValueUtil from 'src/utils/operatorValue.util';
import stringUtil from 'src/utils/string.util';

import { Columns } from '../V2DriversPresenter/Columns';

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (maybeDriver: DriverEntity | null) => void;
  selectedCompanyId: number;
  currentDriverId?: number;
};

const SelectDriverDialog: FC<Props> = memo((
  {
    open,
    onClose,
    onSelect,
    currentDriverId,
    selectedCompanyId,
  }
) => {
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(30);
  const [rowCount, setRowCount] = useState<number>(0);
  const [sortCondition, setSortCondition] = useState<string>('');
  const [orConditions, setOrConditions] = useState<RansackPredicateEntity[]>([]);
  const [andConditions, setAndConditions] = useState<RansackPredicateEntity[]>([]);
  const [searchCondition, setSearchCondition] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const { data: requestData, isLoading: requestIsLoading } = useDriversRequest(
    page,
    sortCondition,
    searchCondition,
    pageSize,
  );

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (sortModel.length === 0) {
      setSortCondition('');
      return;
    }

    setSortCondition([stringUtil.toSnakeCase(sortModel[0].field), sortModel[0].sort].join('+'));
  };

  const onFilterChange = (filterModel: GridFilterModel) => {
    const items = filterModel?.items;

    if (!items || items.length === 0) {
      setOrConditions([]);
      setAndConditions([]);
      return;
    }

    const ransackPredicates: RansackPredicateEntity[] = items.flatMap((item: GridFilterItem) => operatorValueUtil.convertToRansackPredicate(
      stringUtil.toSnakeCase(item.field),
      item.operator,
      item.value as string | string[] | undefined,
      filterModel.logicOperator
    ));

    setOrConditions(ransackPredicates.filter((predicate) => predicate?.link === 'or'));
    setAndConditions(ransackPredicates.filter((predicate) => predicate?.link === 'and'));
  };

  // openEditDialog を渡さないので edit 列は Columns 側で落ちる。
  const dataGridColumns: GridColDef[] = Columns();

  const onSelectionModelChange = (rowSelectionModel: GridRowSelectionModel) => {
    if (rowSelectionModel.type !== 'include') return;
    setSelectedIds(Array.from(rowSelectionModel.ids).map((id) => Number(id)));
  };

  const maybeSelectedDriver = requestData?.data.find((e) => selectedIds.includes(e.id));

  const onColumnWidthChange = (params: GridColumnResizeParams) => {
    dataGridUtil.setSavedColumnWidth('driver-columns', params);
  };

  const onSubmit = () => {
    onSelect(maybeSelectedDriver ?? null);
    onClose();
  };

  useEffect(() => {
    const companyPredicate: RansackPredicateEntity | null = selectedCompanyId ? {
      column: 'company_id',
      predicate: 'eq',
      value: `${selectedCompanyId}`,
      link: 'and'
    } : null;

    // 通常(非 temporary)ドライバーのみ。value は falsy 判定で落ちないよう文字列 'false'。
    const temporaryPredicate: RansackPredicateEntity = {
      column: 'temporary',
      predicate: 'eq',
      value: 'false',
      link: 'and'
    };

    const modifiedAndConditions: RansackPredicateEntity[] = [
      ...(companyPredicate ? [companyPredicate] : []),
      temporaryPredicate,
      ...andConditions,
    ];

    setSearchCondition(
      [
        operatorValueUtil.convertToQueryParams('or_conditions', orConditions),
        operatorValueUtil.convertToQueryParams('and_conditions', modifiedAndConditions),
      ].filter((maybe) => maybe).join('&')
    );
  }, [andConditions, orConditions, selectedCompanyId]);

  useEffect(() => {
    if (!requestData) return;
    setRowCount(requestData?.totalCount || 0);
  }, [requestData]);

  // 現在の通常ドライバーの事前選択。初回オープンは driver 一覧取得が非同期で、rows が空の間に
  // DataGrid が事前選択を prune してしまう（2回目はキャッシュ済みで間に合う）。そのため
  // 「データ到着後、rows に該当 driver が含まれた時」に一度だけ seed する。
  // ダイアログは開閉ごとに再マウントされ seededRef も毎回リセットされるため、開くたびに再 seed される。
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (!currentDriverId) {
      seededRef.current = true;
      return;
    }
    if (requestData?.data.some((driver) => driver.id === currentDriverId)) {
      setSelectedIds([currentDriverId]);
      seededRef.current = true;
    }
  }, [currentDriverId, requestData]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen sx={{ mt: 8 }}>
      <DialogTitle>
        <Typography variant="h6">
          通常ドライバーを選択してください
        </Typography>
      </DialogTitle>
      <DataGridPremium
        rows={requestData?.data || []}
        columns={dataGridColumns}
        pagination
        disableRowGrouping
        slots={{ loadingOverlay: CustomLoadingOverlay }}
        localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
        loading={requestIsLoading}
        onColumnWidthChange={(params) => onColumnWidthChange(params)}
        onRowSelectionModelChange={onSelectionModelChange}
        rowSelectionModel={{ ids: new Set(selectedIds), type: 'include' }}
        sortingMode="server"
        paginationMode="server"
        filterMode="server"
        rowCount={rowCount}
        pageSizeOptions={[30]}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model) => {
          setPage(model.page);
          setPageSize(model.pageSize);
          setSelectedIds([]);
        }}
        onSortModelChange={handleSortModelChange}
        onFilterModelChange={onFilterChange}
        sx={{ mb: 2, mx: 2 }}
        disableAggregation
        disablePivoting
      />
      <DialogActions>
        {currentDriverId != null && (
          <Button
            onClick={() => { onSelect(null); onClose(); }}
            variant="contained"
            color="error"
            sx={{ mr: 'auto' }}
          >
            未設定にする
          </Button>
        )}
        <Button onClick={onClose} color="primary">
          キャンセル
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          color="primary"
          disabled={!maybeSelectedDriver}
        >
          {maybeSelectedDriver ? `${maybeSelectedDriver.name} を設定する` : 'ドライバーを選択してください'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default SelectDriverDialog;
